import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";
import { useGetCreateJobStatus } from "@/lib/api/dorman";
import { MimeType } from "@/lib/api/utils";
import { UploadPresignedFile } from "@/lib/file";
import { UploadIcon } from "lucide-react";
import { useRef, useEffect, useState } from "react";
import { UploadReportValidation, useGenerateCfrSuggestionsReportUpload } from "@/lib/api/cfr";
import { useQueryClient } from "@tanstack/react-query";

type ReportType = "direct_ship" | "purchase_release";

export function UploadSuggestionsReportDialog() {
  const { toast } = useToast();
  const [link, setLink] = useState<string | undefined | null>();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [reportType, setReportType] = useState<ReportType>("direct_ship");
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const [cropYear, setCropYear] = useState<number | undefined>(undefined);
  const [jobId, setJobId] = useState<string | undefined>(undefined);
  const jobStatusRes = useGetCreateJobStatus(jobId);
  const createUploadJob = useGenerateCfrSuggestionsReportUpload();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onGenerateReport = async (e: React.FormEvent<HTMLFormElement>) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    try {
      if (!fileToUpload) { return; }
      if (isGenerating) { return; }
      setIsGenerating(true);

      const validationData: {
        filename: string;
        mime_type: any;
        report_type: ReportType;
        crop_year?: number;
      } = {
        filename: fileToUpload?.name || "",
        mime_type: fileToUpload?.type as any || "",
        report_type: reportType
      };

      if (reportType === "purchase_release") {
        validationData.crop_year = cropYear;
      }

      const parseDataResult = UploadReportValidation.safeParse(validationData);

      if (!parseDataResult.success) {
        toast({
          title: "Invalid File"
        })
        return;
      }

      const result = await createUploadJob(parseDataResult.data);
      if (!result.success) {
        throw result;
      }

      const resultData = result.data;
      setJobId(resultData.job_id);

      const uploadResult = await UploadPresignedFile(resultData.link, fileToUpload);
      if (!result.success) {
        console.error("Failed to upload report", uploadResult);
        toast({
          title: "Failed to upload report"
        })
        return;
      }

      toast({
        title: "Processing now.",
        description: "",
      });
      setIsOpen(false);
    } catch (error) {
      setLink(null);
      console.error("Failed to generate cfr suggestions upload report", error);

      toast({
        title: "Failed to generate report",
        description: "",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const hasFetchedJob = !!jobStatusRes?.data || false;
  const jobStatus = jobStatusRes?.data?.data?.job_status;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(status) => {
        if (!status) { 
          setLink(null);
          setCropYear(undefined);
        }
        if (status) {
          setJobId(undefined);
        }
        setIsOpen(status);
      }}
    >
      <DialogTrigger>
        <span className="sr-only">Upload Cash Sales Report</span>
        <Button className="gap-2" asChild>
          <span>
            {!jobId && (
              <>
                <UploadIcon className="h-4 w-4" />
                Upload Report
              </>
            )}
            {jobId && <JobStatus jobStatus={jobStatus} setJobId={setJobId} />}
          </span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Upload CFR Suggestions Report
          </DialogTitle>
          <DialogDescription>
            Select the report type and upload the corresponding file
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-6" onSubmit={onGenerateReport}>
          <Tabs
            value={reportType}
            onValueChange={(value) => {
              setReportType(value as ReportType);
              setFileToUpload(null);
              setCropYear(undefined);
              if (fileInputRef.current) {
                fileInputRef.current.value = "";
              }
            }}
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="direct_ship">Direct Ship</TabsTrigger>
              <TabsTrigger value="purchase_release">Purchase Release</TabsTrigger>
            </TabsList>

            <TabsContent value="direct_ship">
              <div className="space-y-2 grid">
                <Label htmlFor="directShipFileInput">Direct Ship Report (Excel)</Label>
                <Input
                  id="directShipFileInput"
                  type="file"
                  ref={fileInputRef}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFileToUpload(event.target.files?.[0] || null)}
                  accept=".xlsx"
                />
              </div>
            </TabsContent>

            <TabsContent value="purchase_release">
              <div className="space-y-4 grid">
                <div className="space-y-2 grid">
                  <Label htmlFor="purchaseReleaseFileInput">Purchase Release Report (CSV)</Label>
                  <Input
                    id="purchaseReleaseFileInput"
                    type="file"
                    ref={fileInputRef}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFileToUpload(event.target.files?.[0] || null)}
                    accept=".csv"
                  />
                </div>
                <div className="space-y-2 grid">
                  <Label htmlFor="cropYearInput">Crop Year</Label>
                  <Input
                    id="cropYearInput"
                    type="number"
                    min="2020"
                    max="2080"
                    value={cropYear || ""}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const value = event.target.value;
                      setCropYear(value ? parseInt(value, 10) : undefined);
                    }}
                    placeholder="Enter crop year"
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
              disabled={isGenerating}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isGenerating}>
              {isGenerating ? "Uploading..." : "Upload"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function JobStatus({ jobStatus, setJobId }: { jobStatus: string; setJobId: any }) {
  const className = "text-xs font-bold tracking-tight";
  const queryClient = useQueryClient();

  useEffect(() => {
    if (jobStatus === "completed") {
      setJobId(undefined);
      queryClient.invalidateQueries({ queryKey: ["cfr", "suggestions"] });
    }
  }, [jobStatus, queryClient])

  if (!jobStatus) { return null }
  if (jobStatus === "queued") { return (<div className={className}>Processing</div>) }
  if (jobStatus === "doesnt_exist") { return (<div className={className}>Failed</div>) }
  if (jobStatus === "failed") { return (<div className={className}>Failed</div>) }
  if (jobStatus === "halt") { return (<div className={className}>Failed</div>) }
  if (jobStatus !== "completed") { return (<div className={className}>Unknown</div>); }

  return null;
}
